import React, { useState, useEffect } from 'react';
import classnames from 'classnames';

import { BaseTextInputProps } from '../BaseTextInput';
import styles from './styles.module.scss';
import InputLayout from '../InputLayout';
import BaseTextInputContainer from '../BaseTextInputContainer';

interface RuleType {
    regexp: RegExp;
    msg: string;
}

interface StatusesType {
    [key: string]: boolean;
}

interface ValidationMessagesProps {
    colorTheme: BaseTextInputProps['colorTheme'];
    rules: Array<RuleType>;
    statuses: StatusesType;
}

interface Props extends BaseTextInputProps {
    className?: string;
    shouldShowRulesWhenEmpty?: boolean;
    rules?: Array<RuleType>;
    button?: {
        icon: string;
        onClick: () => void;
    };
    onValidityChange?: (isValid: boolean) => void;
}

const ValidationMessages: React.FC<ValidationMessagesProps> = ({ colorTheme, statuses, rules }) => {
    const className = classnames(styles.validationMessages, {
        [styles[colorTheme]]: !!colorTheme,
    });

    return (
        <div className={className}>
            {rules.map(({ msg }, idx) => (
                <div key={`validation-msg-${idx}`} className={classnames(styles.rule, { [styles.validRule]: statuses[idx] })}>
                    <i className={classnames('icon-ico_pw_checking', styles.icon)} />
                    <span>{msg}</span>
                </div>
            ))}
        </div>
    );
};

const BaseRealTimeCheckingInput: React.FC<Props> = (props) => {
    const { label, labelWidth, button, colorTheme, error, isDisabled, onChange: propOnChange, onValidityChange, ...inputProps } = props;

    const [value, setValue] = useState('');
    const [ruleValidationStatuses, setRuleValidationStatuses] = useState<StatusesType>({});
    const [isValid, setIsValid] = useState<boolean>(false);

    const getValidationStatuses = (val) => props.rules.reduce((s, rule, index) => ({ ...s, [index]: rule.regexp.test(val || '') }), {});

    const checkValidityFromStatuses = (statuses) => !Object.values(statuses).includes(false);

    const onChange = (e: any) => {
        const {
            target: { value: val },
        } = e;
        const wasValid = isValid;
        const statuses = getValidationStatuses(val);
        const newIsValid = checkValidityFromStatuses(statuses);

        // Update states
        setValue(val);
        setRuleValidationStatuses(statuses);
        setIsValid(newIsValid);

        if (wasValid !== newIsValid && onValidityChange) {
            onValidityChange(newIsValid);
        }

        if (propOnChange) {
            propOnChange(e);
        }
    };

    useEffect(() => {
        // Initialize ruleValidationStatuses
        const statuses = getValidationStatuses(props.value);
        setRuleValidationStatuses(statuses);
        setIsValid(checkValidityFromStatuses(statuses)); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.rules]);

    return (
        <InputLayout label={label} labelWidth={labelWidth} colorTheme={colorTheme} name={props.name} error={error}>
            <BaseTextInputContainer className={styles.baseRealTimeCheckingInput} error={error} isDisabled={isDisabled} colorTheme={colorTheme}>
                <input
                    className={`text-input-input ${styles.input}`}
                    event-id={props.name}
                    disabled={isDisabled}
                    onChange={onChange}
                    {...inputProps}
                />
                {/* TODO move button input to a better component */}
                {!!button && (
                    <div className={styles.button} onClick={button.onClick} aria-hidden="true">
                        <i className={classnames(button.icon, styles.buttonIcon)} />
                    </div>
                )}
            </BaseTextInputContainer>
            {(props.shouldShowRulesWhenEmpty || !!value) && !!props.rules.length && (
                <ValidationMessages colorTheme={colorTheme} rules={props.rules} statuses={ruleValidationStatuses} />
            )}
        </InputLayout>
    );
};

BaseRealTimeCheckingInput.defaultProps = {
    className: '',
    rules: [],
    shouldShowRulesWhenEmpty: true,
};
BaseRealTimeCheckingInput.displayName = 'BaseRealTimeCheckingInput';

export default BaseRealTimeCheckingInput;
